import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import FormError from '../../components/shared/FormError';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const validateEmail = (value: string) => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
    } catch {
      // Silently handle — don't reveal if email exists
    } finally {
      setLoading(false);
      setSent(true);
      toast.success('If an account exists with that email, a reset link has been sent.');
    }
  };

  return (
    <div className="relative min-h-screen bg-white overflow-hidden font-sans select-none">
      {/* Background Brand Anchor (Large Vertical Text) - Desktop Only */}
      <div className="absolute top-0 right-0 h-full hidden xl:flex items-center pointer-events-none pr-12">
        <h1 className="text-[min(12vw,14vh)] font-bold text-slate-100 uppercase tracking-tighter transform rotate-[-90deg] origin-right translate-y-1/2 leading-none opacity-70 whitespace-nowrap">
          Sales
        </h1>
      </div>

      <div className="container relative mx-auto px-6 lg:px-12 py-12 min-h-screen flex flex-col justify-center max-w-[1440px]">
        {/* Glow Effect Element */}
        <div
          className="absolute pointer-events-none opacity-60"
          style={{
            top: '-14%',
            left: '-28%',
            width: '80%',
            height: '26%',
            background: 'radial-gradient(circle at top left, #0099d681, #0000 60%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="grid lg:grid-cols-2 gap-20 2xl:gap-32 items-center w-full relative z-10">
          {/* Main Content Area (Sales Branding) */}
          <div className="max-w-2xl space-y-10">
            {/* Branding Section */}
            <div className="space-y-4">
              <img
                src="/truvideo-logo-dark.png"
                alt="TruVideo"
                className="w-auto h-8 lg:h-10"
              />
              <div className="h-0.5 w-12 bg-primary rounded-full"></div>
            </div>

            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                Account <br className="hidden sm:block" />
                <span className="text-primary font-black">Recovery Hub.</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-400 max-w-lg leading-relaxed font-medium italic">
                Securely reset your password and regain access to your quality analysis dashboard.
              </p>
            </div>

            {/* Technical Metadata Labels */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-slate-100 max-w-lg">
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Recovery</div>
                <div className="text-3xl font-black text-primary">Secure</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Method</div>
                <div className="text-3xl font-black text-primary">Email</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase mb-2">Status</div>
                <div className="text-xl font-bold italic text-slate-400">Verified</div>
              </div>
            </div>

            <div className="flex gap-4 items-center pt-8">
              <div className="h-px w-12 bg-slate-200"></div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-bold">
                Identity Security
              </span>
            </div>
          </div>

          {/* Form Section */}
          <div className="w-full flex justify-center lg:justify-start">
            <div className="w-full max-w-lg">
              <div className="w-full">
                {sent ? (
                  <div className="space-y-10">
                    <div className="mb-12">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-green-50 rounded-xl">
                          <CheckCircle className="h-7 w-7 text-green-600" />
                        </div>
                        <h3 className="text-xl lg:text-2xl font-bold text-slate-900 tracking-[0.05em] uppercase">Check Your Email</h3>
                      </div>
                      <p className="text-slate-400 text-sm font-medium leading-relaxed">
                        If an account exists for <span className="text-slate-900 font-bold">{email}</span>, we've sent a password reset link.
                        The link expires in 1 hour.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <Link
                        to="/login"
                        className="w-full sm:w-auto h-14 px-8 rounded-xl bg-slate-900 text-white font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-primary transition-all"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Login
                      </Link>
                      <button
                        onClick={() => { setSent(false); setEmail(''); }}
                        className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-primary transition-colors"
                      >
                        Try another email
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="space-y-8">
                    <div className="mb-12 flex items-center gap-4">
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <Mail className="h-7 w-7 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl lg:text-2xl font-bold text-slate-900 tracking-[0.05em] uppercase">Forgot Password</h3>
                        <p className="text-slate-400 text-xs font-semibold tracking-wide uppercase mt-1">Enter your email to receive a password reset link</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="space-y-3">
                        <label htmlFor="reset-email" className="text-sm font-semibold text-slate-700 ml-1">
                          Business Email <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="reset-email"
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                            placeholder="NAME@ENTERPRISE.COM"
                            className={`w-full h-14 rounded-xl border px-6 text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-4 transition-all outline-none ${
                              emailError
                                ? 'border-red-300 bg-red-50/50 focus:border-red-400 focus:ring-red-500/5'
                                : 'border-slate-100 bg-slate-50 focus:bg-white focus:border-primary focus:ring-primary/5'
                            }`}
                            aria-invalid={!!emailError}
                          />
                          <FormError message={emailError} />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6 pt-4">
                      <button
                        type="submit"
                        disabled={loading || !email.trim()}
                        className="w-full h-16 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:translate-y-[-1px] active:translate-y-[0px] transition-all bg-slate-900 text-white hover:bg-primary cursor-pointer flex items-center justify-center gap-3 uppercase tracking-[0.1em]"
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Processing...</span>
                          </div>
                        ) : (
                          "Send Reset Link"
                        )}
                      </button>

                      <div className="flex justify-center">
                        <Link
                          to="/login"
                          className="font-bold text-xs tracking-widest text-primary hover:text-primary/70 transition-colors uppercase flex items-center gap-2"
                        >
                          <ArrowLeft className="h-3 w-3" />
                          Back to Login
                        </Link>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 lg:mt-0 lg:absolute lg:bottom-12">
          <p className="text-[10px] font-bold text-slate-300 tracking-[0.2em] uppercase">
            &copy; 2026 TruVideo &bull; All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
